import {MarkerLayer} from "features/apartmentViewer/ApartmentViewer/layers/MarkerLayer";
import {ArrowLayer} from "features/apartmentViewer/ApartmentViewer/layers/ArrowLayer";
import {PerspectiveCamera, Plane, Raycaster, Vector2, Vector3} from "three";
import {CameraHandler} from "features/apartmentViewer/ApartmentViewer/services/CameraHandler";
import {EventManager} from "features/apartmentViewer/ApartmentViewer/services/EventManager";

export interface HitData {
    hitPoint: Vector3;
    directionXZ: Vector3;
    isFloor: boolean;
}

export class UserNavigationContainer {

    constructor(
        private cameraHandler: CameraHandler,
        private eventManager: EventManager,
        private arrowLayer: ArrowLayer,
        private markerLayer: MarkerLayer,
        private rayCaster: Raycaster = new Raycaster(),
        private hitPlane: Plane = new Plane(new Vector3(0, 1, 0), 0)
    ) {
    }

    calculateMouseHitpoint(camera: PerspectiveCamera, mousePosition: Vector2, floorLevel: number) : HitData {
        this.rayCaster.setFromCamera(mousePosition, camera);
        this.hitPlane.constant = -floorLevel;

        const hitPoint = new Vector3();
        const intersected = this.rayCaster.ray.intersectPlane(this.hitPlane, hitPoint);
        let isFloor = true;

        if (!intersected) {
            isFloor = false;
            const adjustedHeight = camera.position.y + (camera.position.y - floorLevel);
            this.hitPlane.constant = -adjustedHeight;
            this.rayCaster.ray.intersectPlane(this.hitPlane, hitPoint);
        }

        const directionXZ = new Vector3(
            hitPoint.x - camera.position.x,
            0,
            hitPoint.z - camera.position.z
        ).normalize();

        return {
            hitPoint,
            directionXZ,
            isFloor
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- marker pick result has dynamic shape from 3D layer
    private getDirectionalPick(direction: Vector3): any {
        return this.markerLayer.pickRaymarch(this.cameraHandler.camera.position, direction);
    }

    private updateArrowLayer(mouseRayHitData: HitData) {
        if (this.arrowLayer) {
            this.arrowLayer.updateArrowVisibility(
                this.cameraHandler.camera,
                mouseRayHitData.hitPoint,
            );
        }
    }

    private getSelectedMarker(hitData: HitData) {
        let pickedMarker = null;

        if (this.eventManager?.MousePick && !this.markerLayer.getPickedMarker()) {
            pickedMarker = this.markerLayer.pickClosest(
                this.cameraHandler.camera.position,
                hitData.hitPoint
            );


            if (!pickedMarker && !hitData.isFloor) {
                pickedMarker = this.markerLayer.pickRaymarch(
                    this.cameraHandler.camera.position,
                    hitData.directionXZ
                );
            }
        }

        if (!pickedMarker) {
            pickedMarker = this.handleDirectionalPicking();
        }

        return pickedMarker;
    }

    private getDirectionMapping(cameraDirection: Vector3): Record<string, Vector3> {
        const leftDirection = new Vector3().crossVectors(cameraDirection, new Vector3(0, 1, 0)).normalize();
        const rightDirection = leftDirection.clone().negate();

        return {
            "KeyW": cameraDirection.clone().multiplyScalar(-1),
            "ArrowUp": cameraDirection.clone().multiplyScalar(-1),
            "KeyS": cameraDirection,
            "ArrowDown": cameraDirection,
            "KeyA": leftDirection,
            "ArrowLeft": leftDirection,
            "KeyD": rightDirection,
            "ArrowRight": rightDirection,
        };
    }

    private tempVector = new Vector3();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- marker pick result has dynamic shape from 3D layer
    private handleDirectionalPicking(): any {
        const cameraDirection = this.tempVector.set(
            Math.sin(this.cameraHandler.camera.rotation.y),
            0,
            Math.cos(this.cameraHandler.camera.rotation.y)
        );

        const directionMap = this.getDirectionMapping(cameraDirection);

        for (const [key, direction] of Object.entries(directionMap)) {
            if (this.eventManager.KeyUp(key)) {
                return this.getDirectionalPick(direction);
            }
        }

        return null;
    }

    update() {
        if (!this.cameraHandler?.siteCamera) return;


        const hitData = this.calculateMouseHitpoint(
            this.cameraHandler.camera,
            this.eventManager.mousePositionNormalized,
            this.cameraHandler.siteCamera.floorVerticalPosition
        );

        this.updateArrowLayer(hitData);
        const selectedMarker = this.getSelectedMarker(hitData);

        this.markerLayer.handleHoveringMarker();

        if (selectedMarker) {
            this.markerLayer.setSelectedMarker(selectedMarker);
        }
    }
}

